import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, input, Input, OnInit, Output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { AppCommonModule } from '@app/components/app.common.module';
import { BaseComponent } from '@app/components/base/base.component';
import { ModalService } from '@app/components/modal/modal.service';
import { Constants } from '@app/constants/Constants';

@Component({
  selector: 'app-new-financial-product-template',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, AppCommonModule, CommonModule],
  templateUrl: './new-financial-product-template.component.html',
  styleUrls: ['./new-financial-product-template.component.css']
})
export class NewFinancialProductTemplateComponent extends BaseComponent implements OnInit {
  @Output() save = new EventEmitter<any>();
  @Input() set data(value: any) {
    if (value) {
      this.form.patchValue(value);
      this.isEditMode = true;
      this.editIndex = value.index;
    } else {
      this.form.reset();
      this.form.get('externalsMinRate')?.setValue(null);
      this.form.get('externalsMaxRate')?.setValue(null);
      this.isEditMode = false;
      this.editIndex = null;
    }
  }
  maxPrice = input<number>(0);

  form: FormGroup;
  term: any[] = [];
  isEditMode = false;
  editIndex: number | null = null;
  currentResidualValue = input<any>(null);
  termList = computed(() => {
    const termList = this.currentResidualValue().map((item: any) => item.term);
    if(!termList) return [];
    return termList;
  });
  isTermUsedSig = signal(false);
  minTerm: number = Constants.MIN_TERMS_FOR_NORMAL_PRODUCT;
  maxTerm: number = Constants.MAX_TERMS_FOR_NORMAL_PRODUCT;

  constructor(private fb: FormBuilder, private modalService: ModalService) {
    super();
  }

  ngOnInit() {
    this.createForm();
    this.setDataFromService();
  }

  createForm() {
    this.form = this.fb.group({
      daimlerRate: [null, Validators.required],
      daimlerFixedAmount: [null, Validators.required],
      externalsMinRate: [null, Validators.required],
      externalsMaxRate: [null, Validators.required],
      term: [null, [Validators.required, Validators.min(this.minTerm), Validators.max(this.maxTerm)]],
    });
  }

  setDataFromService() {
    if (this.modalService.data) {
      this.form.patchValue(this.modalService.data);
      this.isEditMode = true;
      this.editIndex = this.modalService.data.index;
    }
  }

  close() {
    this.modalService.closeModal();
  }

  saved() {
    const formValue = this.form.getRawValue();
    if (this.isEditMode && this.editIndex !== null) {
      formValue.index = this.editIndex;
    }
    this.save.emit(formValue);
    this.close();
  }

  handleTermChange() {
    const term = Number(this.form.get('term')?.value);
    if (!term) return;
    const find = this.termList().find((condition: any) => condition === term);
    this.isTermUsedSig.set(!!find);
  }

  isTermUsed(): boolean {
    const term = this.form.get('term')?.value;
    if (!term) return false;
    const find = this.termList().find((condition: any) => condition === term);
    return !!find;
  }

  handleDaimlerRateValidation() {
    const daimlerRate = parseFloat(this.form.get('daimlerRate')?.value);
    if(isNaN(daimlerRate)){
      return;
    }
    if(daimlerRate < 0){
  }
    if(daimlerRate > 100){
      this.form.get('daimlerRate')?.setValue(100);
    }
  }

  handleExternalsMinRate() {
    const externalsMinRate = parseFloat(this.form.get('externalsMinRate')?.value);
    const externalsMaxRate = parseFloat(this.form.get('externalsMaxRate')?.value);
    if(isNaN(externalsMinRate)){
      return;
    }
    if(externalsMinRate < 0){
      this.form.get('externalsMinRate')?.setValue(0);
    }
    if(externalsMinRate > 100){
      this.form.get('externalsMinRate')?.setValue(100);
    }
    if(isNaN(externalsMaxRate)){
      return;
    }
    if(externalsMaxRate < 0){
      this.form.get('externalsMaxRate')?.setValue(0);
    }
    if(externalsMaxRate > 100){
      this.form.get('externalsMaxRate')?.setValue(100);
    }

    if(externalsMinRate > externalsMaxRate){
      this.form.get('externalsMinRate')?.setValue(externalsMaxRate);
    }
  }

  handleFixedAmount() {
    const maxPrice = this.maxPrice();
    const updatedFixedAmount = this.form.get('daimlerFixedAmount')?.value;
    if(maxPrice){
      const updatePercentage = updatedFixedAmount * 100 / maxPrice;
      this.form.get('daimlerRate')?.setValue(updatePercentage?.toFixed(2));
    }
    this.hideAmountInput('daimlerFixedAmount');
  }

  handleDaimlerRate() {
    this.handleDaimlerRateValidation();
    const maxPrice = this.maxPrice();
    const updatedDaimlerRate = this.form.get('daimlerRate')?.value;
    if(maxPrice){
      const updatePercentage = maxPrice * (updatedDaimlerRate / 100);
      this.form.get('daimlerFixedAmount')?.setValue(updatePercentage?.toFixed(2));
    }
  }
}

